import { makeStyles } from "@mui/styles";
import { Box, Divider } from '@mui/material';
import React, { ReactNode, useEffect, useRef, useState } from "react";

import { Orientation, SCALE_FACTOR } from "./utils";
import pagify, { PageBreak } from "./utils/pagify";

const useStyles = makeStyles({
  landscapePage: {
    height: `${8.5 * SCALE_FACTOR}in`,
    width: `${11 * SCALE_FACTOR}in`,
    paddingTop: `${0.3 * SCALE_FACTOR}in`,
    padding: `${0.5 * SCALE_FACTOR}in`,
  },
  portraitPage: {
    height: `${11 * SCALE_FACTOR}in`,
    width: `${8.5 * SCALE_FACTOR}in`,
    paddingTop: `${0.3 * SCALE_FACTOR}in`,
    padding: `${0.5 * SCALE_FACTOR}in`,
  },
  hidden: {
    position: "fixed",
    visibility: "hidden",
  },
  hiddenHeader: {
    position: "fixed",
    width: "100%",
    visibility: "hidden",
  },
});

interface PagedContainerProps {
  header: ReactNode;
  sortedComponents: ReactNode[];
  orientation: Orientation;
  onChangePageDivs: (divs: HTMLDivElement[]) => void;
}

const PagedContainer = ({ header, sortedComponents, orientation, onChangePageDivs }: PagedContainerProps) => {
  const classes = useStyles();
  const [pageBreaks, setPageBreaks] = useState<PageBreak[]>([]);
  const [availableContentHeight, setAvailableContentHeight] = useState<number>();
  const dummyPageRef = useRef<HTMLDivElement>(null);
  const dummyHeaderRef = useRef<HTMLDivElement>(null);
  const pageDivsRef = useRef<HTMLDivElement[]>();
  const calculatingPageDivsRef = useRef(true);

  const paginate = (rootNodeElement: HTMLDivElement, headerElement: HTMLDivElement) => {
    const { maxContentHeight, pageBreaks} = pagify({
      rootNodeElement,
      headerElement,
      orientation,
    });
    pageDivsRef.current = new Array(pageBreaks.length).fill(undefined);
    calculatingPageDivsRef.current = true;
    setPageBreaks(pageBreaks);
    setAvailableContentHeight(maxContentHeight);
  };

  useEffect(() => {
    if (dummyPageRef.current && dummyHeaderRef.current) {
      paginate(dummyPageRef.current, dummyHeaderRef.current);
    }
  }, [header, sortedComponents, orientation, setPageBreaks]);

  const renderPage = (firstComponentIdx: number, lastComponentIdx: number) => {
    const height = availableContentHeight ? `${availableContentHeight * SCALE_FACTOR}in` : undefined;
    return (
      <div key={`page-components-${firstComponentIdx}-${lastComponentIdx}`}>
        {header}
        <Box sx={{ height, maxHeight: height, overflow: "visible" }}>
          {sortedComponents.slice(firstComponentIdx, lastComponentIdx)}
        </Box>
      </div>
    );
  };

  const updatePageDivs = (div: HTMLDivElement, idx: number) => {
    if (calculatingPageDivsRef.current && pageDivsRef.current) {
      pageDivsRef.current[idx] = div;
      if (pageDivsRef.current.every(d => !!d)) {
        calculatingPageDivsRef.current = false;
        onChangePageDivs(pageDivsRef.current);
      }
    }
  };

  const pageClassName = orientation === "portrait" ? classes.portraitPage : classes.landscapePage;

  return (
    <>
      <Box>
        <Box className={classes.hiddenHeader} ref={dummyHeaderRef}>
          {header}
        </Box>
        <Box className={classes.hidden} ref={dummyPageRef}>
          {sortedComponents}
        </Box>
      </Box>
      {pageBreaks
        .reduce((currPages: React.ReactElement[], nextComponentBreak: PageBreak, i) => {
          return [...currPages, renderPage(pageBreaks[i - 1] ? pageBreaks[i - 1].componentIndex : 0, nextComponentBreak.componentIndex)];
        }, [])
        .map((page, idx) => (
          <Box key={`page${idx}`}>
            <div className={pageClassName} ref={d => d && updatePageDivs(d, idx)}>
              {page}
            </div>
            <Divider />
          </Box>
        ))}
    </>
  );
};

export default PagedContainer;
